import { createSelector } from '@reduxjs/toolkit';
import type { Client, ActiveVisitor, SelfPublicUser, SelfUser } from 'types';
import type { AppState } from './reducers';
import type { AuthState } from './auth-slice';
import {
  sortAndFilterSelfFromClientList,
  makeFakeTourActiveVisitor,
  filterClientList,
  sortClientList,
  makeDissociatedVisitor,
} from './data-util';

export const internalSelectActiveUser = (state: AuthState): SelfUser | null => {
  if (!state.account) return null;
  if (!state.activeOrgId) return state.account.users[0];
  const activeUser = state.account.users.find(u => u.org.id === state.activeOrgId);
  return activeUser || state.account.users[0];
};

export const internalSelectActivePublicUser = (
  state: AuthState,
): SelfPublicUser | null => {
  if (!state.publicAccount) return null;
  if (!state.activeOrgId) return state.publicAccount.users[0];
  const activeUser = state.publicAccount.users.find(u => u.org.id === state.activeOrgId);
  return activeUser || state.publicAccount.users[0];
};

export const selectAuthedAccount = (s: AppState) => s.auth.account;

export const selectActiveUser = (s: AppState) => internalSelectActiveUser(s.auth);
export const selectActivePublicUser = (s: AppState) =>
  internalSelectActivePublicUser(s.auth);
export const selectActivePublicAccount = (s: AppState) => s.auth.publicAccount;

export const selectVisitorList = createSelector(
  (state: AppState) => state.realtime.visitorMap,
  selectActiveUser,
  (state: AppState) => state.realtime.dissociatingFromSbid,
  (visitorMap, user, dissociatingFromSbid) => {
    let list = Object.entries(visitorMap).map(([_, v]) => {
      if (dissociatingFromSbid && dissociatingFromSbid === v.sbid) {
        // modify the returned list, but not the redux state itself
        return makeDissociatedVisitor(v);
      } else {
        return v;
      }
    });

    list = sortAndFilterSelfFromClientList(list, user);
    return list;
  },
);

export const selectVisitorListCountByStatus = createSelector(
  selectVisitorList,
  visitorList => {
    const activeVisitors = visitorList.filter(v => v.isPageVisible);
    return {
      all: visitorList.length,
      active: activeVisitors.length,
      away: visitorList.length - activeVisitors.length,
    };
  },
);

export const selectActiveVisitorList = createSelector(selectVisitorList, visitorList => {
  return visitorList.filter(v => v.isPageVisible);
});

export const selectAwayVisitorList = createSelector(selectVisitorList, visitorList => {
  return visitorList.filter(v => !v.isPageVisible);
});

export const selectFilteredVisitorList = createSelector(
  (state: AppState) => state.dashboard.activeFilterUniqueVisitors,
  (state: AppState) => state.realtime.visitorMap,
  (state: AppState) => state.dashboard.activeFilter,
  (dbFilteredVisitors, rtVisitorMap, filter) => {
    let list = [] as Client[];
    if (!filter) {
      return list;
    }

    // Turn realtime visitor map into list, db list into full Client objects.
    const localVisitorList = Object.entries(rtVisitorMap).map(([_, vis]) => vis);
    const dbVisitorList = dbFilteredVisitors.map(fv => ({
      ...fv,
      ...rtVisitorMap[fv.sbid],
    }));

    // Combine and dedupe the lists, prioritize dbVisitorList entries.
    list = [...dbVisitorList, ...localVisitorList];
    list = list.filter(
      (v1, idx, arr) => idx === arr.findIndex(v2 => v1.sbid === v2.sbid),
    );

    // Run the filters on the list locally to account for updating visitors
    return sortClientList(filterClientList(list, filter));
  },
);

export const selectActiveVisitorId = (s: AppState) => s.dashboard.activeVisitorId;

// TODO - have this check sbids are same before merging???
export const selectActiveVisitor = createSelector(
  (state: AppState) => state.dashboard.activeVisitor,
  (state: AppState) => state.realtime.visitorMap,
  (state: AppState) => state.realtime.optimisticInteractions,
  (state: AppState) => state.realtime.widgetState,
  (state: AppState) => state.tour.isTouring,
  (visitor, realtimeVisitorMap, optimisticInteractions, widgetState, isTouring) => {
    if (isTouring) {
      return makeFakeTourActiveVisitor();
    }
    if (visitor) {
      const rtVisitor = realtimeVisitorMap[visitor.sbid];
      const updatedVisitor: ActiveVisitor = {
        widgetState,
        ...rtVisitor,
        ...visitor,
        ...optimisticInteractions,
      };
      if (rtVisitor && rtVisitor.email && !visitor.email) {
        // when email gets added it gets updated on the realtime object, make
        // sure that gets into the result here
        updatedVisitor.email = rtVisitor.email;
      }
      return updatedVisitor;
    }
    return visitor;
  },
);
